
import { useState, useEffect } from "react";
import { RecipeInput } from "@/components/RecipeInput";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { ConversionResult } from "@/types/recipe";
import Hero from "@/components/home/Hero";
import Stats from "@/components/home/Stats";
import RecipeSection from "@/components/home/RecipeSection";
import HomeFooter from "@/components/home/HomeFooter";

const Index = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [recipe, setRecipe] = useState<ConversionResult | null>(null);
  const [originalInput, setOriginalInput] = useState<{ url?: string; text?: string }>({});

  const isValidUrl = (urlString: string) => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (e) {
      return false;
    }
  };

  const extractRecipeContent = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Look for structured data in the page (JSON-LD)
    const jsonLdScripts = tempDiv.querySelectorAll('script[type="application/ld+json"]');
    for (let i = 0; i < jsonLdScripts.length; i++) {
      const script = jsonLdScripts[i];
      try {
        const jsonContent = JSON.parse(script.textContent || '{}');
        // Check if it contains recipe data
        if (jsonContent['@type'] === 'Recipe' || 
            (Array.isArray(jsonContent['@graph']) && 
             jsonContent['@graph'].some((item: any) => item['@type'] === 'Recipe'))) {
          // Return the JSON as a string for processing
          return script.textContent || '';
        }
      } catch (e) {
        // Continue if JSON parsing fails
        console.error('Failed to parse JSON-LD script:', e);
      }
    }
    
    // If no JSON-LD found, try to find recipe content in HTML
    const recipeContent = 
      tempDiv.querySelector('.recipe') || 
      tempDiv.querySelector('.recipe-content') || 
      tempDiv.querySelector('[itemtype*="Recipe"]') ||
      tempDiv.querySelector('.entry-content') ||
      tempDiv.querySelector('article');
    
    if (recipeContent) {
      // Cast to HTMLElement to access innerText property
      const htmlElement = recipeContent as HTMLElement;
      return htmlElement.textContent || htmlElement.innerText || html.substring(0, 50000);
    }
    
    const mainContent = 
      tempDiv.querySelector('main') || 
      tempDiv.querySelector('#content') ||
      tempDiv.querySelector('.content');
    
    if (mainContent) {
      // Cast to HTMLElement to access innerText property
      const htmlElement = mainContent as HTMLElement;
      return htmlElement.textContent || htmlElement.innerText || html.substring(0, 50000);
    }
    
    const cleanHtml = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '');
    
    return cleanHtml.substring(0, 50000);
  };

  const processRecipeWithOpenAI = async (content: string, temperatureUnit: string): Promise<ConversionResult> => {
    const apiKey = localStorage.getItem('openai_api_key');
    if (!apiKey) {
      const key = prompt("Please enter your OpenAI API key to process this recipe:");
      if (!key) throw new Error("OpenAI API key is required");
      localStorage.setItem('openai_api_key', key);
    }
    
    const systemPrompt = `You are a helpful AI that extracts recipe information from text or HTML.

    Extract the following information in a structured format:
    - Title: The name of the recipe
    - Description: A brief description of the dish
    - Ingredients: With amounts, units, and names clearly separated
    - Steps: Detailed cooking instructions with temperature and time information when available
    - Cooking time: prepTime, cookTime, totalTime in minutes or hours format
    - Servings: How many people the recipe serves (extract the exact number, do not default to 1)
    - Notes: Any additional tips or variations
    - Cuisine type: The cuisine origin (Italian, French, etc.)
    - Food type: Main category (Chicken, Dessert, etc.)
    - Temperatures: Extract ALL temperature values (in ${temperatureUnit}) used throughout the recipe
    
    Format temperatures in ${temperatureUnit} degrees.
    
    For ingredients:
    - Extract precise amounts (numeric values)
    - Convert all units to standard measurements (teaspoon, tablespoon, cup, ounce, pound, gram, etc.)
    - Separate the ingredient name from its preparation method
    - For fractions in amounts, use decimal representation (1/4 = 0.25, 1/3 = 0.33, etc.)
    
    For cooking steps:
    - Extract temperature values when present (like "Preheat oven to 350°F")
    - Extract timing information (like "bake for 25 minutes")
    
    For servings:
    - Look for explicit statements like "serves 4", "makes 12 cakes", "yield: 6 servings"
    - Check JSON-LD data for recipeYield or yield attributes
    - Do not default to 1 - make a best estimate based on the recipe
    
    Return the data in this JSON format:
    {
      "title": "Recipe Title",
      "description": "Recipe Description",
      "servings": 4,
      "prepTime": "10 minutes",
      "cookTime": "30 minutes",
      "totalTime": "40 minutes",
      "ingredients": [
        {"amount": 1, "unit": "cup", "name": "ingredient name"}
      ],
      "steps": [
        {"instruction": "Step instruction", "temperature": "350", "time": "25 minutes"}
      ],
      "notes": ["note 1", "note 2"],
      "cuisine_type": "Italian",
      "food_type": "Pasta",
      "temperatures": ["350", "425"]
    }
    
    Make sure to include ALL temperatures mentioned anywhere in the recipe in the "temperatures" array.
    
    Units must be one of: teaspoon, tablespoon, cup, ounce, pound, gram, kilogram, milliliter, liter, pinch, piece, slice, whole.
    Cuisine type must be one of: Italian, Lebanese, Greek, Indian, Chinese, Japanese, Thai, Mexican, French, Spanish, American, Mediterranean, Middle Eastern, Korean, Vietnamese, Turkish, Brazilian, Caribbean, African, British, Other.
    Food type must be one of: Chicken, Dessert, Snacks, Vegetables, Ground Beef, Seafood, Soup, Pork, Other.`;

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('openai_api_key')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: systemPrompt
            },
            {
              role: 'user',
              content
            }
          ],
          temperature: 0.3,
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      try {
        const contentText = result.choices[0].message.content;
        
        let jsonStr;
        
        const jsonCodeBlockMatch = contentText.match(/```json\n([\s\S]*?)\n```/);
        if (jsonCodeBlockMatch) {
          jsonStr = jsonCodeBlockMatch[1];
        } else {
          const jsonObjectMatch = contentText.match(/\{[\s\S]*\}/);
          if (jsonObjectMatch) {
            jsonStr = jsonObjectMatch[0];
          } else {
            jsonStr = contentText.replace(/```[a-z]*\n?|\n```/g, '').trim();
          }
        }
        
        jsonStr = jsonStr.replace(/(\d+)\/(\d+)/g, function(match, numerator, denominator) {
          return (parseFloat(numerator) / parseFloat(denominator)).toString();
        });
        
        const jsonData = JSON.parse(jsonStr);
        
        if (!jsonData.title || !jsonData.ingredients || !jsonData.steps) {
          throw new Error('Parsed data is missing required fields');
        }

        // Ensure servings is at least 1 if it was missed or parsed as 0
        if (!jsonData.servings || jsonData.servings < 1) {
          jsonData.servings = 1;
        }

        // Convert temperatures to air fryer settings
        if (jsonData.temperatures && jsonData.temperatures.length > 0) {
          // Filter out any non-numeric or invalid temperature values
          const validTemperatures = jsonData.temperatures.filter(
            (temp: string) => temp && !isNaN(Number(temp)) && temp.trim() !== ''
          );
          
          if (validTemperatures.length > 0) {
            const airFryerSettings = await convertToAirFryer(validTemperatures, temperatureUnit);
            jsonData.airFryerSettings = airFryerSettings;
          } else {
            jsonData.airFryerSettings = [];
          }
        } else {
          jsonData.airFryerSettings = [];
        }
        
        return jsonData;
      } catch (parseError) {
        throw new Error('Could not parse recipe data from AI response: ' + parseError.message);
      }
    } catch (error) {
      throw error;
    }
  };

  const convertToAirFryer = async (temperatures: string[], unit: string): Promise<{original: string, airfryer: string}[]> => {
    try {
      const result: {original: string, airfryer: string}[] = [];
      
      // For each temperature, look up the air fryer equivalent from the database
      for (const temp of temperatures) {
        // Skip empty or non-numeric temperature values
        if (!temp || temp.trim() === '' || isNaN(Number(temp))) {
          continue;
        }
        
        const tempValue = parseInt(temp, 10);
        
        // Query the air_fryer_rules table for conversion rules
        const { data, error } = await supabase
          .from('air_fryer_rules')
          .select('details')
          .eq('rule_type', 'temperature')
          .single();
        
        if (error) {
          console.error('Error fetching air fryer rules:', error);
          // Use a default conversion instead of showing "no conversion available"
          const defaultAirfryerTemp = unit === 'F' ? tempValue - 25 : tempValue - 15;
          result.push({ 
            original: `${temp}°${unit}`, 
            airfryer: `${defaultAirfryerTemp}°${unit}` 
          });
          continue;
        }
        
        if (data && data.details) {
          // Type guard to check if details has the right structure
          const details = data.details;
          let airfryerTemp = tempValue;
          let conversionFound = false;
          
          // Check if details is an object with a conversions array
          if (typeof details === 'object' && details !== null && 'conversions' in details && Array.isArray(details.conversions)) {
            // Find the appropriate range for this temperature
            for (const conversion of details.conversions) {
              if (
                conversion && 
                typeof conversion === 'object' &&
                'min' in conversion && 
                'max' in conversion && 
                typeof conversion.min === 'number' && 
                typeof conversion.max === 'number' &&
                tempValue >= conversion.min && 
                tempValue <= conversion.max
              ) {
                if (unit === 'F') {
                  const reduction = typeof conversion.reduction_f === 'number' ? conversion.reduction_f : 25;
                  airfryerTemp = tempValue - reduction;
                } else {
                  const reduction = typeof conversion.reduction_c === 'number' ? conversion.reduction_c : 15;
                  airfryerTemp = tempValue - reduction;
                }
                conversionFound = true;
                break;
              }
            }
          }
          
          if (!conversionFound) {
            // Default conversion if no specific rule found
            if (unit === 'F') {
              airfryerTemp = tempValue - 25;  // Default 25°F lower
            } else {
              airfryerTemp = tempValue - 15;  // Default 15°C lower
            }
          }
          
          result.push({ 
            original: `${temp}°${unit}`, 
            airfryer: `${airfryerTemp}°${unit}` 
          });
        } else {
          // Fallback if no data or details found
          let airfryerTemp = unit === 'F' ? tempValue - 25 : tempValue - 15;
          result.push({ 
            original: `${temp}°${unit}`, 
            airfryer: `${airfryerTemp}°${unit}` 
          });
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error converting to air fryer temperatures:', error);
      // Create a better fallback that doesn't produce "NaNnones"
      return temperatures
        .filter(temp => temp && !isNaN(Number(temp)) && temp.trim() !== '')
        .map(temp => {
          const tempValue = parseInt(temp, 10);
          const airfryerTemp = unit === 'F' ? tempValue - 25 : tempValue - 15;
          
          return { 
            original: `${temp}°${unit}`, 
            airfryer: `${airfryerTemp}°${unit}` 
          };
        });
    }
  };
  
  const fetchContentFromUrl = async (url: string): Promise<string> => {
    const corsProxies = [
      'https://corsproxy.io/?',
      'https://api.allorigins.win/raw?url=',
      'https://cors-anywhere.herokuapp.com/'
    ];
    
    let lastError;
    
    for (const proxy of corsProxies) {
      try {
        const proxyUrl = `${proxy}${encodeURIComponent(url)}`;
        
        const response = await fetch(proxyUrl, { 
          timeout: 10000 // 10 seconds timeout
        } as RequestInit);
        
        if (response.status === 404) {
          throw new Error(`The recipe URL was not found (404). Please verify the URL is correct.`);
        }
        
        if (response.status === 403) {
          throw new Error(`Access denied (403). The website might be blocking access to recipe data.`);
        }
        
        if (!response.ok) {
          throw new Error(`Failed to fetch URL: ${response.status} ${response.statusText}`);
        }
        
        const content = await response.text();
        
        if (content.length < 500) {
          throw new Error("Retrieved content is too short, likely an error page");
        }
        
        return content;
      } catch (error: any) {
        lastError = error;
        continue;
      }
    }
    
    if (lastError) {
      if (lastError.message.includes('404')) {
        throw new Error("The recipe URL was not found. Please verify the URL is correct and try again.");
      } else if (lastError.message.includes('403')) {
        throw new Error("The website is blocking access to its recipes. Please try copying and pasting the recipe text directly instead.");
      } else if (lastError.message.includes('timeout') || lastError.message.includes('aborted')) {
        throw new Error("Request timed out. The website might be slow or blocking requests. Try a different URL or paste the recipe text directly.");
      } else if (lastError.message.includes('failed to fetch')) {
        throw new Error("Connection failed. This may be due to network issues or the website blocking our request. Try a different URL or paste the recipe text directly.");
      }
      
      throw lastError;
    }
    
    throw new Error("Failed to fetch recipe from URL after trying multiple methods. Please try pasting the recipe text directly.");
  };

  const handleSubmit = async (input: string, type: "url" | "text") => {
    setIsLoading(true);
    setRecipe(null);

    try {
      if (type === "url" && !isValidUrl(input)) {
        toast.error("Please enter a valid recipe URL starting with http:// or https://");
        setIsLoading(false);
        return;
      }

      const temperatureUnit = localStorage.getItem("temperatureUnit") || "F";
      
      let content: string;
      
      if (type === "url") {
        try {
          const rawContent = await fetchContentFromUrl(input);
          content = extractRecipeContent(rawContent);
          console.log(`Original content length: ${rawContent.length}, Trimmed content length: ${content.length}`);
        } catch (fetchError: any) {
          throw new Error(`${fetchError.message}`);
        }
      } else {
        content = input;
      }
      
      const result = await processRecipeWithOpenAI(content, temperatureUnit);
      
      result.temperature_unit = temperatureUnit as "F" | "C";
      
      setRecipe(result);
      setOriginalInput({
        [type]: input
      });
      
      toast.success("Recipe converted successfully!");
      
    } catch (error: any) {
      if (error.message?.includes("The recipe URL was not found")) {
        toast.error("The recipe URL was not found. Please verify the URL is correct and try again.");
      } else if (error.message?.includes("website is blocking access")) {
        toast.error("The website is blocking access to its recipes. Please try copying and pasting the recipe text directly.");
      } else if (error.message?.includes("timeout") || error.message?.includes("timed out")) {
        toast.error("The connection timed out. Please try a different URL or paste the recipe text directly.");
      } else if (error.message?.includes("API key")) {
        toast.error("Invalid OpenAI API key. Please provide a valid API key.");
        localStorage.removeItem('openai_api_key');
      } else if (error.message?.includes("context_length_exceeded") || error.message?.includes("maximum context length")) {
        toast.error("The recipe page is too large to process. Please try copying just the recipe text and pasting it directly.");
      } else {
        toast.error(error instanceof Error ? error.message : "Something went wrong while processing your recipe. Please try again with a different input.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
    toast.success("Preparing recipe for printing...");
  };

  // Function to automatically set API key
  const checkAndSetApiKey = () => {
    if (localStorage.getItem('openai_api_key')) {
      return; // Key already exists
    }
    
    console.info('API key not found, setting it automatically...');
    
    try {
      // Default key for the application
      const defaultApiKey = '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';
      
      // Store in localStorage
      localStorage.setItem('openai_api_key', defaultApiKey);
      
    } catch (error) {
      console.error('Error setting API key:', error);
    }
  };

  // Set the API key on component mount
  useEffect(() => {
    checkAndSetApiKey();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white via-gray-50/50 to-gray-100">
      <Header />
      <main className="flex-1">
        <div className="container max-w-7xl mx-auto px-4">
          <Hero />
          <div className="max-w-2xl mx-auto -mt-6 space-y-6">
            <RecipeInput onSubmit={handleSubmit} isLoading={isLoading} />
            <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
            <Stats />
          </div>
          <div className="mt-12">
            <RecipeSection 
              recipe={recipe}
              originalInput={originalInput}
              onPrint={handlePrint}
            />
            <HomeFooter />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
